/**
 * OathExternalContactModal - Add/edit an external agency contact
 * OathUI version of externalContactModal.jsx
 * Used by PreferencesModal for managing agency notification contacts
 * Shared between both requesting and posting sides
 */

import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  OathModal,
  OathButton,
  OathInput,
  OathAlert,
} from './OathUI';

const OathExternalContactModal = ({ isOpen, onClose, contact, onSubmit, agencyId }) => {
  const [formData, setFormData] = useState({
    description: contact?.description || '',
    email: contact?.email || '',
    phone: contact?.phone || '',
    isActive: contact?.isActive || false,
    canPost: contact?.canPost ?? true,
    canRequest: contact?.canRequest ?? false,
  });
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Reset form data when the contact prop changes (e.g., when editing a new contact)
  useEffect(() => {
    if (contact) {
      setFormData({
        description: contact.description || '',
        email: contact.email || '',
        phone: contact.phone || '',
        isActive: contact.isActive || false,
        canPost: contact.canPost ?? true,
        canRequest: contact.canRequest ?? false,
      });
    } else {
      setFormData({
        description: '',
        email: '',
        phone: '',
        isActive: false,
        canPost: true,
        canRequest: false,
      });
    }
    setError('');
  }, [contact]);

  const handleToggle = (field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Helper function to validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Helper function to validate phone number format
  const isValidPhoneNumber = (phone) => {
    const phoneRegex = /^\d{10}$/; // Ensures phone number has exactly 10 digits
    return phoneRegex.test(phone);
  };

  const handleSubmit = async () => {
    setError('');

    // Ensure at least one contact field (email or phone) is provided
    if (!formData.email && !formData.phone) {
      setError('Please provide either an email or phone number.');
      return;
    }

    // Validate email format if email is provided
    if (formData.email && !isValidEmail(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    // Validate phone number format if phone is provided
    if (formData.phone && !isValidPhoneNumber(formData.phone)) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }

    setIsSaving(true);
    try {
      // Prepare the contact object
      const contactPayload = {
        agentContactId: contact?.agentContactId || 0, // agentContactId will determine if it's an add or update
        agencyId,
        ...formData,
      };

      // Call the onSubmit function passed from the parent
      await onSubmit(contactPayload);
      toast.success(contact ? 'Contact updated successfully!' : 'Contact added successfully!');
      onClose(); // Close the modal after submission
    } catch {
      toast.error('Failed to submit the contact. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <OathModal
      isOpen={isOpen}
      onClose={onClose}
      title={contact ? 'Edit Contact' : 'Add Contact'}
      size="md"
      footer={
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:ml-auto">
          <OathButton
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
            className="w-full sm:w-auto"
          >
            Cancel
          </OathButton>
          <OathButton
            onClick={handleSubmit}
            loading={isSaving}
            className="w-full sm:w-auto"
          >
            {isSaving ? 'Saving...' : contact ? 'Update Contact' : 'Add Contact'}
          </OathButton>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <OathAlert variant="error" message={error} />}

        <OathInput
          label="Name"
          value={formData.description}
          onChange={(val) => setFormData((prev) => ({ ...prev, description: val }))}
          placeholder="Contact name"
          required
        />

        <OathInput
          label="Email"
          type="email"
          value={formData.email}
          onChange={(val) => setFormData((prev) => ({ ...prev, email: val }))}
          placeholder="email@example.com"
        />

        <OathInput
          label="Phone (only digits)"
          value={formData.phone}
          onChange={(val) => setFormData((prev) => ({ ...prev, phone: val.replace(/\D/g, '') }))}
          placeholder="1234567890"
          helperText="10-digit phone number"
        />

        {/* Toggle switches */}
        <div className="space-y-3 pt-2">
          <label className="flex items-center gap-3 cursor-pointer">
            <button
              type="button"
              role="switch"
              aria-checked={formData.isActive}
              onClick={() => handleToggle('isActive')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.isActive ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                  formData.isActive ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <button
              type="button"
              role="switch"
              aria-checked={formData.canPost}
              onClick={() => handleToggle('canPost')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.canPost ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                  formData.canPost ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-300">Can Post (Access Posting Features)</span>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <button
              type="button"
              role="switch"
              aria-checked={formData.canRequest}
              onClick={() => handleToggle('canRequest')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.canRequest ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                  formData.canRequest ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-300">Can Request (Access Requesting Features)</span>
          </label>
        </div>
      </div>
    </OathModal>
  );
};

export default OathExternalContactModal;
